import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect, useRef } from "react";
import { ArrowLeft, Copy, Pin, ChevronDown, Wallet } from "lucide-react";
import { DataTable, type Column } from "@/components/ui/DataTable.js";
import { StatusBadge } from "@/components/ui/StatusBadge.js";
import { WebchatsDeCompany } from "@/components/webchat/WebchatsDeCompany.js";
import { api } from "@/lib/api.js";
import { relativeTime } from "@/lib/format.js";
import { toast } from "sonner";

interface Note {
  id: number;
  authorName: string | null;
  body: string;
  pinned: boolean;
  createdAt: string;
}

interface Channel {
  idcanal: number;
  nombre: string;
  provider: string;
  estado: string;
}

export function CompanyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const companyId = Number(id);
  const qc = useQueryClient();
  const [newNote, setNewNote] = useState("");
  const [actionsOpen, setActionsOpen] = useState(false);
  const actionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (actionsRef.current && !actionsRef.current.contains(e.target as Node)) {
        setActionsOpen(false);
      }
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const activarCartera = useMutation({
    mutationFn: async () => {
      const wsBase = import.meta.env.VITE_WS_ISOFT_URL ?? "http://localhost:5001";
      const res = await fetch(`${wsBase}/api/mm/habilitar-cartera`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ idcompany: companyId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.estado !== 1) {
        throw new Error(data?.mensaje ?? data?.error ?? `Error ${res.status} al activar cartera`);
      }
      return data as {
        estado: number;
        mensaje: string;
        data?: { idfunnel?: number; funnel_creado?: boolean };
      };
    },
    onSuccess: (data) => toast.success(data.mensaje ?? "Cartera activada"),
    onError: (err: Error) => toast.error(err.message),
  });

  function handleActivarCartera() {
    setActionsOpen(false);
    activarCartera.mutate();
  }

  const company = useQuery({
    queryKey: ["company", companyId],
    queryFn: () => api.get<{ idcompany: number; name: string; country: string | null; status: string | null; distributor: string | null; channelCount: number; notesCount: number }>(`/companies/${companyId}`),
  });
  const channels = useQuery({
    queryKey: ["company", companyId, "channels"],
    queryFn: () => api.get<{ items: Channel[] }>(`/companies/${companyId}/channels`),
  });
  const notes = useQuery({
    queryKey: ["company", companyId, "notes"],
    queryFn: () => api.get<{ items: Note[] }>(`/notes/company/${companyId}`),
  });

  const addNote = useMutation({
    mutationFn: () =>
      api.post(`/notes/company/${companyId}`, { body: newNote, pinned: false }),
    onSuccess: () => {
      toast.success("Nota agregada");
      setNewNote("");
      qc.invalidateQueries({ queryKey: ["company", companyId, "notes"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const channelCols: Column<Channel>[] = [
    { header: "ID canal", render: (r) => <span className="mono text-xs">{r.idcanal}</span> },
    { header: "Proveedor", render: (r) => <span className="badge-neutral">{r.provider}</span> },
    { header: "Nombre", render: (r) => r.nombre ?? "—" },
    { header: "Estado", render: (r) => <StatusBadge value={r.estado} /> },
  ];

  return (
    <div className="space-y-6">
      <Link to="/companies" className="text-sm text-slate-500 hover:text-slate-700 inline-flex items-center gap-1">
        <ArrowLeft size={14} />
        Volver a companies
      </Link>

      <div className="card p-6 flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-lg bg-brand-500 text-white grid place-items-center font-semibold text-lg">
            {company.data?.name?.slice(0, 2).toUpperCase() ?? "??"}
          </div>
          <div>
            <h1 className="text-2xl font-semibold">{company.data?.name ?? "—"}</h1>
            <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
              <button
                className="mono text-xs inline-flex items-center gap-1 hover:text-slate-700"
                onClick={() => {
                  navigator.clipboard.writeText(String(companyId));
                  toast.success("ID copiado");
                }}
              >
                ID {companyId} <Copy size={10} />
              </button>
              <span>·</span>
              <span>{company.data?.country ?? "—"}</span>
              <span>·</span>
              <span>{company.data?.distributor ?? "—"}</span>
              <StatusBadge value={company.data?.status} />
            </div>
          </div>
        </div>

        <div className="relative" ref={actionsRef}>
          <button
            className="btn-secondary inline-flex items-center gap-2"
            onClick={() => setActionsOpen((o) => !o)}
            aria-haspopup="menu"
            aria-expanded={actionsOpen}
          >
            Acción
            <ChevronDown size={14} />
          </button>
          {actionsOpen && (
            <div
              role="menu"
              className="absolute right-0 mt-2 w-56 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg z-10 py-1"
            >
              <button
                role="menuitem"
                className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-800 inline-flex items-center gap-2 disabled:opacity-50"
                onClick={handleActivarCartera}
                disabled={activarCartera.isPending}
              >
                <Wallet size={14} className="text-slate-500" />
                {activarCartera.isPending ? "Activando…" : "Activar cartera"}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        <section className="card col-span-2">
          <header className="px-5 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
            <div>
              <h2 className="font-semibold">Canales</h2>
              <p className="text-xs text-slate-500">
                {company.data?.channelCount ?? 0} canales conectados
              </p>
            </div>
          </header>
          <div className="p-5">
            <DataTable
              columns={channelCols}
              rows={channels.data?.items}
              loading={channels.isLoading}
              getKey={(r) => r.idcanal}
              empty="Esta company no tiene canales todavía."
            />
          </div>
        </section>

        <section className="card">
          <header className="px-5 py-4 border-b border-slate-100 dark:border-slate-800">
            <h2 className="font-semibold">Notas internas</h2>
            <p className="text-xs text-slate-500">
              Anotaciones privadas del equipo sobre esta company.
            </p>
          </header>
          <div className="p-5 space-y-4">
            <div className="space-y-2">
              <textarea
                className="input min-h-[80px]"
                placeholder="Escribe una nota…"
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
              />
              <button
                className="btn-primary w-full"
                disabled={!newNote.trim() || addNote.isPending}
                onClick={() => addNote.mutate()}
              >
                Agregar nota
              </button>
            </div>
            <ul className="space-y-3 max-h-[380px] overflow-y-auto">
              {notes.data?.items.length === 0 && (
                <li className="text-xs text-slate-400 text-center py-6">
                  Sin notas todavía.
                </li>
              )}
              {notes.data?.items.map((n) => (
                <li
                  key={n.id}
                  className="border border-slate-100 dark:border-slate-800 rounded-md p-3 text-sm"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{n.authorName ?? "—"}</span>
                    {n.pinned && <Pin size={12} className="text-amber-500" />}
                  </div>
                  <p className="mt-1 text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{n.body}</p>
                  <p className="text-xs text-slate-400 mt-1">{relativeTime(n.createdAt)}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </div>

      <WebchatsDeCompany idcompany={companyId} />
    </div>
  );
}
